// aluno/oratoria.js — portal oratória (programação de aulas por turma).
//
// Origem (monólito): linhas 5095-5948 (HTML oratoriaScreen + JS renderOratoria,
//   openOratoriaModal e dados das turmas de oratória).
//
// Dependências:
//   - api            (core/api.js)         — api.getProgramacao() com cache 24h
//   - auth           (core/auth.js)        — auth.getCurrent()
//   - notify         (core/notify.js)      — notify.error, notify.loading
//   - router         (core/router.js)      — router.go('/aluno')
//   - oratoria-render (aluno/oratoria-render.js) — renderizarProgramacao, injetarEstilos
//
// Formato da API (getProgramacao):
//   Array de { ano, data, dia_semana, turno, professor, materia, turmas:[int] }
//   Filtro: turmas.includes(Number(currentUser.turma))
//
// Exporta:
//   function render(container)
//
// Cache: api.getProgramacao() já persiste em storage com TTL 24h (Fase 4).
// Este módulo apenas consome o helper — sem lógica de cache própria.

import { api }    from '../core/api.js';
import { auth }   from '../core/auth.js';
import { notify } from '../core/notify.js';
import { router } from '../core/router.js';
import {
  renderizarProgramacao,
  injetarEstilos,
} from './oratoria-render.js';

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Filtra as aulas da programação para a turma do aluno logado.
 * turmas no payload são inteiros; auth.getCurrent().turma é string.
 *
 * @param {Array<object>} todasAulas
 * @param {string|number}  turma
 * @returns {Array<object>} aulas filtradas, ordenadas por data ascendente
 */
function _filtrarPorTurma(todasAulas, turma) {
  const turmaInt = Number(turma);
  if (isNaN(turmaInt)) return [];

  return todasAulas
    .filter(a => Array.isArray(a.turmas) && a.turmas.includes(turmaInt))
    .sort((a, b) => (a.data < b.data ? -1 : a.data > b.data ? 1 : 0));
}

// ── Estado de carregamento ────────────────────────────────────────────────────

/**
 * Exibe skeleton de carregamento na área principal.
 * @param {HTMLElement} container
 */
function _mostrarCarregando(container) {
  const main = container.querySelector('#orat-main');
  if (!main) return;
  main.innerHTML = `
    <div style="text-align:center;padding:60px 20px;color:#9B9B9B">
      <div style="font-size:36px;margin-bottom:12px">⏳</div>
      <div style="font-size:15px;font-weight:700">Carregando programação...</div>
      <div style="font-size:12px;margin-top:8px">Usando cache de 24h quando disponível.</div>
    </div>`;
}

/**
 * Exibe mensagem de erro com botão de retry.
 * @param {HTMLElement} container
 */
function _mostrarErro(container) {
  const main = container.querySelector('#orat-main');
  if (!main) return;
  main.innerHTML = `
    <div style="text-align:center;padding:60px 20px;color:#ff3b30">
      <div style="font-size:36px;margin-bottom:12px">⚠️</div>
      <div style="font-size:15px;font-weight:700">Falha ao carregar a programação.</div>
      <div style="font-size:13px;margin-top:8px;color:#9B9B9B">Verifique sua conexão e tente novamente.</div>
      <button id="orat-retry-btn" style="margin-top:20px;padding:12px 28px;background:linear-gradient(90deg,#6A001F,#B3002D);color:#fff;border:none;border-radius:12px;font-family:'Montserrat',sans-serif;font-size:13px;font-weight:700;cursor:pointer">
        Tentar novamente
      </button>
    </div>`;

  const retryBtn = container.querySelector('#orat-retry-btn');
  if (retryBtn) {
    retryBtn.addEventListener('click', () => _carregarEExibir(container));
  }
}

// ── Carregamento e exibição ───────────────────────────────────────────────────

/**
 * Busca a programação via API (com cache 24h) e popula a tela.
 * @param {HTMLElement} container
 */
async function _carregarEExibir(container) {
  const user = auth.getCurrent();
  if (!user) {
    router.go('/login');
    return;
  }

  // Admin não tem turma numérica — mostra todas as aulas
  const turma   = user.turma;
  const isAdmin = turma === 'Admin';

  _mostrarCarregando(container);

  const fecharLoading = notify.loading('Carregando programação...');

  try {
    const todasAulas = await api.getProgramacao();
    fecharLoading();

    const aulasDoAluno = isAdmin ? todasAulas : _filtrarPorTurma(todasAulas, turma);

    // Atualiza indicador de total na barra de info
    const infoEl = container.querySelector('#orat-info-total');
    if (infoEl) {
      infoEl.textContent = isAdmin
        ? `${todasAulas.length} aula${todasAulas.length !== 1 ? 's' : ''} (todas as turmas)`
        : `${aulasDoAluno.length} aula${aulasDoAluno.length !== 1 ? 's' : ''} na sua turma`;
    }

    renderizarProgramacao(container, aulasDoAluno);

  } catch (err) {
    fecharLoading();
    notify.error('Falha ao carregar programação. Tente novamente.');
    console.error('[oratoria] erro ao carregar programação:', err);
    _mostrarErro(container);
  }
}

// ── Função principal exportada ────────────────────────────────────────────────

/**
 * Renderiza o portal oratória dentro de `container`.
 * Chamado pelo router para a rota #/aluno/oratoria.
 *
 * @param {HTMLElement} container - elemento #app
 */
export function render(container) {
  const user    = auth.getCurrent();
  const turma   = user?.turma ?? '—';
  const isAdmin = turma === 'Admin';
  const subtitulo = isAdmin ? 'Todas as turmas' : `Turma ${turma}`;

  // Injeta CSS scoped (idempotente)
  injetarEstilos();

  container.innerHTML = `
    <div id="orat-root">

      <!-- Header -->
      <header class="orat-header">
        <div class="orat-header-inner">
          <button id="orat-back-btn" type="button"
            style="width:38px;height:38px;border-radius:50%;border:none;background:rgba(255,255,255,0.18);color:white;font-size:18px;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:background 0.2s;"
            aria-label="Voltar">←</button>
          <div class="orat-brand" style="flex:1;margin-left:12px">
            <span class="orat-brand-label">Programação</span>
            <span class="orat-brand-title">Turmas Oratória</span>
          </div>
          <div style="width:42px;height:42px;background:rgba(255,255,255,0.15);border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:20px;">🎤</div>
        </div>
      </header>

      <!-- Info de turma + total de aulas -->
      <div style="padding:12px 20px 0;font-size:12px;font-weight:700;color:#6B6B6B">
        ${subtitulo} · <span id="orat-info-total">carregando...</span>
      </div>

      <!-- Main: preenchido por _carregarEExibir() -->
      <main class="orat-main" id="orat-main" style="padding:16px 20px 80px">
        <div style="text-align:center;padding:60px 20px;color:#9B9B9B">
          <div style="font-size:36px;margin-bottom:12px">⏳</div>
          <div style="font-size:15px;font-weight:700">Carregando programação...</div>
        </div>
      </main>

    </div>
  `;

  // Botão voltar
  container.querySelector('#orat-back-btn').addEventListener('click', () => {
    router.go('/aluno');
  });

  // Carrega dados
  _carregarEExibir(container);
}
